/**
 * MandantRedeem — Landing nach Magic-Link-Klick durch den Mandanten.
 *
 * Flow (siehe supabaseInvitations.ts Doc-Header):
 *   1. Mandant klickt Magic-Link in der Email
 *   2. Supabase tauscht den OTP-Code gegen eine Session ein und leitet
 *      auf /mandant/redeem?token=… weiter
 *   3. Diese Komponente zeigt einen DSGVO-Hinweis und einen expliziten
 *      „Einladung annehmen"-Button — KEIN auto-Redeem ohne Klick (DSGVO:
 *      Einwilligung muss aktiv erfolgen)
 *   4. Bei Klick: redeemInvitation(token) → bekommt caseId zurück
 *   5. Redirect auf /mandant/case/{caseId}
 *
 * Edge-Cases:
 *   - kein Token in URL → klare Fehlermeldung, kein redeemBefore
 *   - User noch nicht eingeloggt (z.B. Magic-Link-OTP fehlgeschlagen) →
 *     Hinweis, dass sie zuerst auf den Anmelde-Link klicken sollen
 *   - Token expired/revoked/already used → freundliche Fehlermeldung mit
 *     Hinweis, eine neue Einladung anzufragen
 */

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ShieldCheck, Mail } from 'lucide-react';
import { de } from '../lib/i18n/de';
import { useAuthOrSession } from '../lib/authOrSession';
import { redeemInvitation } from '../lib/supabaseInvitations';

type RedeemState =
  | { kind: 'idle' }
  | { kind: 'redeeming' }
  | { kind: 'success'; caseId: string }
  | { kind: 'error'; message: string };

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function MandantRedeem() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user, isLoading, isMock } = useAuthOrSession();
  const [state, setState] = useState<RedeemState>({ kind: 'idle' });

  const token = params.get('token') ?? '';
  const tokenIsSyntacticallyValid = token.length > 0 && UUID_RE.test(token);

  // Nach Erfolg: kurze Verzögerung, damit User die Bestätigung lesen kann
  useEffect(() => {
    if (state.kind !== 'success') return;
    const handle = setTimeout(() => {
      void navigate(`/mandant/case/${state.caseId}`, { replace: true });
    }, 900);
    return () => {
      clearTimeout(handle);
    };
  }, [state, navigate]);

  async function handleAccept() {
    if (!tokenIsSyntacticallyValid) {
      setState({ kind: 'error', message: de.mandant.redeem.missingTokenBody });
      return;
    }
    setState({ kind: 'redeeming' });
    try {
      const { caseId } = await redeemInvitation(token);
      setState({ kind: 'success', caseId });
    } catch (err) {
      // Wir nehmen jeden Server-Fehler als „Token ungültig/abgelaufen" an —
      // der Server liefert je nach Pfad unterschiedliche Codes (siehe Migration
      // 0015), aber für den Mandanten ist die Botschaft immer dieselbe:
      // „Bitten Sie den Berater um eine neue Einladung."
      const raw = err instanceof Error ? err.message : '';
      console.warn('MandantRedeem: redeem failed', raw);
      setState({ kind: 'error', message: de.mandant.redeem.errorExpiredBody });
    }
  }

  // -- Render-Pfade --

  // Kein Token in URL → Sackgasse
  if (!tokenIsSyntacticallyValid) {
    return (
      <RedeemFrame>
        <Title text={de.mandant.redeem.missingTokenTitle} />
        <Body text={de.mandant.redeem.missingTokenBody} tone="error" />
      </RedeemFrame>
    );
  }

  // Auth-Status noch ungeklärt
  if (isLoading) {
    return (
      <RedeemFrame>
        <Title text={de.mandant.redeem.title} />
        <Body text="Sitzung wird geprüft …" tone="muted" />
      </RedeemFrame>
    );
  }

  // Im Production-Pfad: User MUSS eingeloggt sein, sonst kann redeem_mandant_invitation
  // nicht greifen (auth.uid() ist NULL → wirft Server-Error). Im Mock erlauben
  // wir den Klick zur lokalen Tests-Erleichterung.
  if (!user && !isMock) {
    return (
      <RedeemFrame>
        <Title text={de.mandant.redeem.notLoggedInTitle} />
        <div className="mt-4 flex items-start gap-3 rounded-sm border border-amber-200 bg-amber-50 px-4 py-3">
          <Mail size={18} className="mt-0.5 flex-shrink-0 text-amber-700" />
          <p className="font-serif text-sm text-amber-900">{de.mandant.redeem.notLoggedInBody}</p>
        </div>
      </RedeemFrame>
    );
  }

  if (state.kind === 'success') {
    return (
      <RedeemFrame>
        <Title text={de.mandant.redeem.successTitle} />
        <Body text={de.mandant.redeem.successBody} tone="success" />
      </RedeemFrame>
    );
  }

  if (state.kind === 'error') {
    return (
      <RedeemFrame>
        <Title text={de.mandant.redeem.errorTitle} />
        <Body text={state.message} tone="error" />
      </RedeemFrame>
    );
  }

  // idle / redeeming
  return (
    <RedeemFrame>
      <Title text={de.mandant.redeem.title} />
      <p className="mt-3 font-serif text-base italic text-stone-500">
        {de.mandant.redeem.subtitle}
      </p>

      <div className="mt-8 rounded-sm border border-stone-200 bg-stone-50 px-5 py-4">
        <div className="flex items-start gap-3">
          <ShieldCheck size={18} className="mt-0.5 flex-shrink-0 text-stone-600" />
          <p className="font-serif text-sm text-stone-700">{de.mandant.redeem.consentLabel}</p>
        </div>
      </div>

      <button
        type="button"
        onClick={() => {
          void handleAccept();
        }}
        disabled={state.kind === 'redeeming'}
        className="mt-6 inline-flex w-full items-center justify-center rounded-sm bg-stone-900 px-6 py-4 font-sans text-sm font-medium uppercase tracking-widest text-white transition-colors hover:bg-stone-800 disabled:cursor-not-allowed disabled:bg-stone-400"
      >
        {state.kind === 'redeeming'
          ? de.mandant.redeem.acceptingButton
          : de.mandant.redeem.acceptButton}
      </button>
    </RedeemFrame>
  );
}

// -----------------------------------------------------------------------------
// Layout-Helper — schmaler zentrierter Frame im Editorial-Stil
// -----------------------------------------------------------------------------

function RedeemFrame({ children }: { children: React.ReactNode }) {
  return (
    <div className="mx-auto max-w-md pb-24 pt-12 md:pt-20">
      <div className="relative">
        <div
          aria-hidden="true"
          className="absolute -right-2 top-0 hidden h-20 w-20 rounded-sm md:block"
          style={{ backgroundColor: 'var(--color-dim-private)', opacity: 0.18 }}
        />
        <p className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
          Tragfähigkeitsprüfung
        </p>
        <div className="relative mt-3">{children}</div>
      </div>
    </div>
  );
}

function Title({ text }: { text: string }) {
  return (
    <h1 className="font-serif text-4xl font-black leading-tight tracking-tight text-stone-900 md:text-5xl">
      {text}
    </h1>
  );
}

function Body({ text, tone }: { text: string; tone: 'success' | 'error' | 'muted' }) {
  const cls =
    tone === 'success' ? 'text-emerald-800' : tone === 'error' ? 'text-red-800' : 'text-stone-500';
  return <p className={`mt-4 font-serif text-base italic ${cls}`}>{text}</p>;
}
